<template>
  <div>
    <Card class="h-full">
      <template #header>
        <div class="flex flex-row px-5 py-3 gap-3 justify-center rounded-t-xl bg-primary-200 dark:bg-primary-700">
          <h1 class="font-bold text-xl my-auto">Additional Fields</h1>
          <IconButton v-show="!editing && canEdit" v-tooltip.top="'Edit'" rounded small icon="pencil" @click="startEditingFlexFields"></IconButton>
        </div>
      </template>
      <template #content>
        <MaintainFlexFields
          v-model:customizeFlexRows="customizeFlexRows"
          v-model:allFields="allFields"
          v-model:flexRowTemplates="flexRowTemplates"
          v-model:selectedFlexRowTemplate="selectedFlexRowTemplate"
          v-model:fields="currentFlexFields"
          :editing="editing"
        ></MaintainFlexFields>
        <SubmitOptions
          v-show="editing"
          :allowSubmit="editing"
          class="pt-10"
          @cancelled="stopEditingFlexFields"
          @submitted="submitFlexFieldChanges"
        ></SubmitOptions>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import type { FlexRowField, FlexRowTemplate } from "@/types/FlexRows";
import { getAllFlexFields, getFlexRowTemplates } from "@/services/FlexRowsService";

const currentFlexFields = defineModel<FlexRowField[]>("currentFlexFields", { required: true });
const editing = defineModel<boolean>("editing", { required: true });
const editingTitle = defineModel<string>("editingTitle", { required: true });

const emit = defineEmits<{
  startEditing: [];
}>();

let currentFlexFieldsHistory = useManualRefHistory(currentFlexFields, { clone: true });
const flexFieldsSubmitted = ref(false);

interface Props {
  canEdit: boolean;
  projectId: string;
  drawId: string;
}
const props = defineProps<Props>();

const customizeFlexRows = ref(false);
const allFields = ref<FlexRowField[]>([]);
const flexRowTemplates = ref<FlexRowTemplate[]>([]);
const selectedFlexRowTemplate = ref<FlexRowTemplate>();

const startEditingFlexFields = (): void => {
  getAllFlexFields().then((result: FlexRowField[]) => (allFields.value = result));
  getFlexRowTemplates().then((result: FlexRowTemplate[]) => (flexRowTemplates.value = result));
  emit("startEditing");
  currentFlexFieldsHistory = useManualRefHistory(currentFlexFields, { clone: true });
  currentFlexFieldsHistory.commit();
  editing.value = true;
  editingTitle.value = "Editing Additional Fields";
};

const submitFlexFieldChanges = (): void => {
  flexFieldsSubmitted.value = true;
  currentFlexFieldsHistory.commit();
  axios.post(`/flex-rows/fields/update/${props.projectId}/${props.drawId}`, currentFlexFields.value).catch((e) => errorApi(e));
  stopEditingFlexFields();
};

const stopEditingFlexFields = (): void => {
  if (!flexFieldsSubmitted.value) {
    const lastHistory = currentFlexFieldsHistory.history.value[0]?.snapshot;
    let lastFields: any;
    if (lastHistory) {
      lastFields = JSON.parse(JSON.stringify(lastHistory));
    }

    // Ensure dates are actual Date objects, as Zod.parse() will fail on strings
    setTimeout(() => {
      lastFields.forEach((field: FlexRowField) => {
        if (field.dateTimeValue) {
          field.dateTimeValue = new Date(field.dateTimeValue);
        }
      });
      currentFlexFields.value = lastFields;
    }, 10);
  }
  flexFieldsSubmitted.value = false;
  editing.value = false;
  editingTitle.value = "";
};
</script>
